import {reservationTicketEmail} from '../../utils/reservation-ticket';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NgForm } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { format } from 'date-fns';
import { TranslateService } from '@ngx-translate/core';
import {
  cilCalendar,
  cilCheck,
  cilCheckCircle,
  cilClock,
  cilUser,
  cilWarning,
  cilXCircle,
} from '@coreui/icons';

import { environment } from '../../../environments/environment';
import {
  DefaultplusService,
  DefaultplusServicesService,
} from '../../services/defaultplus-services.service';

const MEETING_BASE_URL = 'https://meeting.sagel.app';

type SupportedLanguage = 'en' | 'tr' | 'es' | 'el' | 'de';

/**
 * Customer-facing DefaultPlus self-booking page.
 *
 * The public sibling of the staff-only `defaultplus-appointment-builder`:
 * same service-first model (service → durationMin → end time, name plus
 * email-or-phone), but reached over a shareable `/appointment/:company` link
 * with no session. The catalog comes from the public
 * `/defaultplus-services/public/:userId` route and every date/time change is
 * validated against `POST /availability/check` (mode=defaultplus) so guests
 * only ever submit a slot the tenant's rules actually allow (V-slot-aware).
 */
@Component({
    selector: 'app-defaultplus-booking',
    templateUrl: './defaultplus-booking.component.html',
    styleUrls: ['./defaultplus-booking.component.scss'],
    standalone: false
})
export class DefaultplusBookingComponent implements OnInit, OnChanges {
  private ticketLink:string|null=null;
  /**
   * The public company slug. Rendered as a child of the shared
   * `/manual-reservation/:companyName` page, so the host passes it in rather
   * than the child reading the route.
   */
  @Input() companyName!: string;

  icons = {
    cilCalendar, cilCheck, cilCheckCircle, cilClock, cilUser, cilWarning, cilXCircle,
  };

  userId!: string;
  private lang: SupportedLanguage = 'en';

  // ── Catalog ────────────────────────────────────────────────────────────
  catalog: DefaultplusService[] = [];
  catalogLoading = false;
  catalogLoadFailed = false;
  selectedService: DefaultplusService | null = null;

  // ── Fields ─────────────────────────────────────────────────────────────
  // The CoreUI date picker works in Date objects; `date`/`time` stay the
  // normalized strings the availability/booking APIs expect (yyyy-MM-dd, HH:mm).
  minDate = new Date();
  dateModel: Date = new Date();
  date = format(this.dateModel, 'yyyy-MM-dd');
  time = '';
  name = '';
  email = '';
  phone = '';
  notes = '';

  // ── Time slots (15-min, availability-aware) ──────────────────────────────
  // Selectable start times are derived from the day's free gaps for the chosen
  // service, so a guest can only pick a slot the tenant's rules actually allow.
  readonly slotStepMin = 15;
  slotsLoading = false;
  slotsUnavailable = false;
  daySlots: string[] = [];
  private slotReq$ = new Subject<void>();

  // ── Closed days (disabled in the date picker) ────────────────────────────
  private readonly weekdayKeys = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
  ];
  private closedWeekdays: Record<string, boolean> = {};
  private closedDateKeys = new Set<string>();
  fullyClosedDates: Date[] = [];
  dateFilter: (date: Date) => boolean = () => true;

  // ── Stepper (2 stages: service & time → your details) ────────────────────
  currentStep = 1;
  readonly totalSteps = 2;

  // ── Status ────────────────────────────────────────────────────────────────
  isSubmitting = false;
  success: boolean | null = null;
  errorMessage = '';
  reservationId = '';
  formError: string | null = null;

  constructor(
    private http: HttpClient,
    private servicesApi: DefaultplusServicesService,
    private translate: TranslateService,
  ) {
    const supported: SupportedLanguage[] = ['en', 'tr', 'es', 'el', 'de'];
    this.translate.addLangs(supported);
    this.translate.setDefaultLang('en');
    const browserLang = navigator.language.split('-')[0] as SupportedLanguage;
    this.lang = supported.includes(browserLang) ? browserLang : 'en';
    this.translate.use(this.lang);
  }

  ngOnInit(): void {
    this.slotReq$.pipe(debounceTime(300)).subscribe(() => this.loadDaySlots());
    if (this.companyName && !this.userId) {
      this.resolveTenant();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyName']?.currentValue && !this.userId) {
      this.resolveTenant();
    }
  }

  private resolveTenant(): void {
    this.catalogLoading = true;
    this.http
      .get<any>(`${environment.baseUrl}/users/get-id-by-company-name`, {
        params: { company_name: this.companyName },
      })
      .subscribe({
        next: (res) => {
          this.userId = res.user_id;
          if (!this.userId) {
            this.catalogLoading = false;
            this.catalogLoadFailed = true;
            return;
          }
          this.loadCatalog();
          this.loadClosures();
        },
        error: () => {
          this.catalogLoading = false;
          this.catalogLoadFailed = true;
        },
      });
  }

  loadCatalog(): void {
    this.catalogLoading = true;
    this.catalogLoadFailed = false;
    this.servicesApi.getPublicServices(this.userId).subscribe({
      next: (services) => {
        this.catalog = services;
        this.catalogLoading = false;
        if (services.length === 1) {
          this.selectService(services[0]);
        }
      },
      error: () => {
        this.catalog = [];
        this.catalogLoading = false;
        this.catalogLoadFailed = true;
      },
    });
  }

  get catalogEmpty(): boolean {
    return !this.catalogLoading && !this.catalogLoadFailed && this.catalog.length === 0;
  }

  selectService(service: DefaultplusService): void {
    this.selectedService = service;
    this.formError = null;
    this.refreshSlots();
  }

  compareServices = (a: DefaultplusService, b: DefaultplusService): boolean =>
    !!a && !!b && a.name === b.name;

  /** End time = start + selected service duration (never a hardcoded +60). */
  get endTime(): string {
    if (!this.selectedService) return '';
    const [h, m] = (this.time || '00:00').split(':').map(Number);
    const total = h * 60 + m + Number(this.selectedService.durationMin || 0);
    const endH = Math.floor(total / 60) % 24;
    const endM = total % 60;
    return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
  }

  get hasContactChannel(): boolean {
    return !!(this.email.trim() || this.phone.trim());
  }

  onDatePick(value: Date | string): void {
    const key = this.toDateKey(value);
    if (key) this.date = key;
    this.refreshSlots();
  }

  selectSlot(slot: string): void {
    this.time = slot;
    this.formError = null;
  }

  // ── Stepper control ───────────────────────────────────────────────────────
  nextStep(): void {
    this.formError = null;
    if (!this.selectedService) {
      this.formError = this.translate.instant('APPOINTMENT_BOOKING.ERRORS.SERVICE_REQUIRED');
      return;
    }
    if (!this.time) {
      this.formError = this.translate.instant('APPOINTMENT_BOOKING.ERRORS.SLOT_REQUIRED');
      return;
    }
    this.currentStep = 2;
  }

  prevStep(): void {
    this.formError = null;
    this.currentStep = 1;
  }

  private toDateKey(value: Date | string): string {
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? '' : format(value, 'yyyy-MM-dd');
    }
    const raw = String(value || '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const m = raw.match(/^(\d{2})[./](\d{2})[./](\d{4})$/);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? '' : format(d, 'yyyy-MM-dd');
  }

  /** Recompute the day's bookable slots whenever the service or date changes. */
  private refreshSlots(): void {
    this.time = '';
    this.daySlots = [];
    this.slotsUnavailable = false;
    if (this.selectedService && this.date) {
      this.slotsLoading = true;
      this.slotReq$.next();
    }
  }

  private loadDaySlots(): void {
    if (!this.selectedService || !this.date || !this.userId) {
      this.slotsLoading = false;
      return;
    }
    // A nominal midday time fetches the whole day's free gaps; the point verdict
    // itself is unused — the gaps drive which 15-min starts we offer.
    this.http
      .post<any>(`${environment.baseUrl}/availability/check`, {
        user_id: this.userId,
        date: this.date,
        time: '12:00',
        service: this.selectedService.name,
        availability_mode: 'defaultplus',
      })
      .subscribe({
        next: (res) => {
          this.slotsLoading = false;
          const gaps = Array.isArray(res?.freeGaps) ? res.freeGaps : [];
          if (gaps.length) {
            this.daySlots = this.slotsFromGaps(gaps);
            this.slotsUnavailable = this.daySlots.length === 0;
          } else if (res?.available === false) {
            // Genuinely no bar this day (closed / fully booked).
            this.daySlots = [];
            this.slotsUnavailable = true;
          } else {
            // Enforcement disabled or failed open (no gaps, not a refusal):
            // offer a default grid and let the server be the final word.
            this.daySlots = this.fallbackSlots();
            this.slotsUnavailable = this.daySlots.length === 0;
          }
        },
        error: () => {
          this.slotsLoading = false;
          this.daySlots = this.fallbackSlots();
          this.slotsUnavailable = false;
        },
      });
  }

  /** 15-min start times that fit inside a free gap, minus already-passed ones. */
  private slotsFromGaps(gaps: any[]): string[] {
    const duration = Number(this.selectedService?.durationMin || 0);
    const step = this.slotStepMin;
    const floor = this.minStartForDate();
    const out: string[] = [];
    for (const g of gaps) {
      const range = this.parseGap(g);
      if (!range) continue;
      let t = Math.ceil(range.startMin / step) * step;
      if (t < floor) t = Math.ceil(floor / step) * step;
      while (t + duration <= range.endMin) {
        out.push(this.minutesToHHMM(t));
        t += step;
      }
    }
    return Array.from(new Set(out)).sort();
  }

  /**
   * The availability endpoint returns freeGaps as formatted "HH:MM–HH:MM"
   * strings (en-dash); tolerate the internal {startMin,endMin} shape too.
   */
  private parseGap(g: any): { startMin: number; endMin: number } | null {
    if (g && Number.isFinite(g.startMin) && Number.isFinite(g.endMin)) {
      return { startMin: g.startMin, endMin: g.endMin };
    }
    const m = String(g || '').match(/(\d{1,2}):(\d{2})\s*[–—-]\s*(\d{1,2}):(\d{2})/);
    if (!m) return null;
    return { startMin: +m[1] * 60 + +m[2], endMin: +m[3] * 60 + +m[4] };
  }

  private fallbackSlots(): string[] {
    const duration = Number(this.selectedService?.durationMin || 0);
    const step = this.slotStepMin;
    const floor = Math.max(8 * 60, this.minStartForDate());
    const end = 21 * 60;
    const out: string[] = [];
    for (let t = Math.ceil(floor / step) * step; t + duration <= end; t += step) {
      out.push(this.minutesToHHMM(t));
    }
    return out;
  }

  /** No past slots when booking for today. */
  private minStartForDate(): number {
    const todayKey = format(new Date(), 'yyyy-MM-dd');
    if (this.date !== todayKey) return 0;
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  }

  private minutesToHHMM(mins: number): string {
    const h = Math.floor(mins / 60) % 24;
    const m = mins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  // ── Closed days ──────────────────────────────────────────────────────────
  /** Weekly closed weekdays + specific closed dates, disabled in the picker. */
  private loadClosures(): void {
    this.http
      .get<any>(`${environment.baseUrl}/closed-days/get?user_id=${this.userId}`)
      .subscribe({
        next: (res) => { this.closedWeekdays = res?.data || {}; this.rebuildDateFilter(); },
        error: () => { this.closedWeekdays = {}; this.rebuildDateFilter(); },
      });
    this.http
      .get<any>(`${environment.baseUrl}/closed-dates/get/${this.userId}`)
      .subscribe({
        next: (res) => {
          const rows: any[] = Array.isArray(res?.data) ? res.data : [];
          this.closedDateKeys = new Set(
            rows.map((r) => this.normalizeDateKey(r?.closed_date)).filter(Boolean),
          );
          this.fullyClosedDates = [...this.closedDateKeys].map((k) => new Date(k + 'T00:00:00'));
          this.rebuildDateFilter();
        },
        error: () => {
          this.closedDateKeys = new Set();
          this.fullyClosedDates = [];
          this.rebuildDateFilter();
        },
      });
  }

  private rebuildDateFilter(): void {
    // New function identity so the picker recomputes its disabled cells.
    this.dateFilter = (date: Date) => {
      if (!date) return true;
      if (this.closedWeekdays[this.weekdayKeys[date.getDay()]]) return false;
      return !this.closedDateKeys.has(this.toDateKey(date));
    };
  }

  private normalizeDateKey(raw: any): string {
    const s = String(raw || '').trim();
    if (/^\d{2}[./]\d{2}[./]\d{4}$/.test(s)) {
      const [d, m, y] = s.split(/[./]/);
      return `${y}-${m}-${d}`;
    }
    return s.slice(0, 10);
  }

  onSubmit(form: NgForm): void {
    this.formError = null;
    if (!this.selectedService) {
      this.formError = this.translate.instant('APPOINTMENT_BOOKING.ERRORS.SERVICE_REQUIRED');
      return;
    }
    if (!this.name.trim()) {
      this.formError = this.translate.instant('APPOINTMENT_BOOKING.ERRORS.NAME_REQUIRED');
      form.form.markAllAsTouched();
      return;
    }
    if (!this.hasContactChannel) {
      this.formError = this.translate.instant('APPOINTMENT_BOOKING.ERRORS.CONTACT_REQUIRED');
      return;
    }
    if (!this.time) {
      this.formError = this.translate.instant('APPOINTMENT_BOOKING.ERRORS.SLOT_REQUIRED');
      return;
    }

    this.isSubmitting = true;
    this.http
      .get<any>(`${environment.baseUrl}/generate-reservation-id?userId=${this.userId}`)
      .subscribe({
        next: (res) => this.createAppointment(res.reservationId),
        error: () => this.handleFailure(),
      });
  }

  private createAppointment(reservationId: string): void {
    const email = this.email.trim();
    const phone = this.phone.trim();
    const [hour, minute] = (this.time || '00:00').split(':').map(Number);

    const payload: any = {
      id: reservationId,
      user_id: this.userId,
      businessType: 'defaultplus',
      date: this.date,
      time: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`,
      source: 'Other',
      source_id: email || phone,
      name: this.name.trim(),
      service: this.selectedService!.name,
      customer_email: email || null,
      customer_phone: phone || null,
      notes: this.notes.trim() || null,
      num_people: 1,
    };

    if (this.selectedService!.meetingEnabled) {
      payload.meeting_url = this.buildMeetingUrl();
    }

    this.http
      .post<any>(`${environment.baseUrl}/api/v1/bookings/appointments`, payload)
      .subscribe({
        next: (res:any) => {
          this.ticketLink=res?.ticket_link||null;
          this.reservationId = reservationId;
          this.success = true;
          this.isSubmitting = false;
          this.sendConfirmationEmail(payload);
        },
        error: (err) => this.handleFailure(err),
      });
  }

  private handleFailure(err?: any): void {
    this.isSubmitting = false;
    this.success = false;
    const backend =
      err?.error?.message || (typeof err?.error === 'string' ? err.error : null);
    this.errorMessage =
      backend || this.translate.instant('APPOINTMENT_BOOKING.ERROR_TEXT');
  }

  resetToForm(): void {
    this.success = null;
    this.errorMessage = '';
    this.currentStep = 1;
  }

  private buildMeetingUrl(): string {
    const companySlug = this.slugify(this.companyName);
    const timePart = (this.time || '00:00').replace(':', '-');
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const tzPart = timezone.replace(/\//g, '_').replace(/\s+/g, '');
    const titleSlug = this.slugify(`${this.selectedService?.name || 'appointment'}-${this.name}`);
    const code = `${titleSlug}-${this.date}_${timePart}-${tzPart}-${this.lang}`;
    return `${MEETING_BASE_URL}/${companySlug}/${encodeURIComponent(code)}`;
  }

  private slugify(value: string): string {
    return (
      (value || 'company')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'company'
    );
  }

  private sendConfirmationEmail(payload: any): void {
    if (!payload.customer_email) return;

    const encodedCompany = encodeURIComponent(this.companyName);
    const cancelLink = `${environment.baseUrl}/#/cancel/${encodedCompany}/${this.reservationId}`;
    const editLink = `${environment.baseUrl}/#/edit/${encodedCompany}/${this.reservationId}`;
    const meetingUrl = payload.meeting_url || '';

    const locales: Record<SupportedLanguage, string> = {
      en: 'en-GB', tr: 'tr-TR', es: 'es-ES', el: 'el-GR', de: 'de-DE',
    };
    const locale = locales[this.lang] ?? 'en-GB';
    const dateObj = new Date(payload.date + 'T00:00:00');
    const longDate = dateObj.toLocaleDateString(locale, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
    const displayRange = `${payload.time.slice(0, 5)} - ${this.endTime}`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

    const phoneRow = payload.customer_phone
      ? `<tr><td style="color:#888;padding:4px 0;vertical-align:top;">${this.translate.instant('APPOINTMENT_BOOKING.MAIL.PHONE')}</td><td style="padding:4px 0;">${payload.customer_phone}</td></tr>`
      : '';
    const notesRow = payload.notes
      ? `<tr><td style="color:#888;padding:4px 0;vertical-align:top;">${this.translate.instant('APPOINTMENT_BOOKING.MAIL.NOTES')}</td><td style="padding:4px 0;">${payload.notes}</td></tr>`
      : '';
    const meetingRow = meetingUrl
      ? `<tr><td style="color:#888;padding:4px 0;vertical-align:top;">${this.translate.instant('APPOINTMENT_BOOKING.MAIL.MEETING')}</td><td style="padding:4px 0;"><a href="${meetingUrl}" style="color:#1a3d8f;word-break:break-all;">${meetingUrl}</a></td></tr>`
      : '';

    const subject = this.translate.instant('APPOINTMENT_BOOKING.MAIL.SUBJECT', {
      service: payload.service,
      company: this.companyName,
    });
    const heading = this.translate.instant('APPOINTMENT_BOOKING.MAIL.HEADING');

    const html = `
<div style="font-family:Arial,sans-serif;background:#f5f5f5;padding:24px 16px;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0"
    style="max-width:560px;margin:0 auto;background:#ffffff;border:1px solid #e0e0e0;border-radius:4px;">
    <tr>
      <td style="padding:24px 32px;border-bottom:2px solid #1a3d8f;">
        <div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:6px;">${this.companyName}</div>
        <div style="font-size:20px;font-weight:600;color:#1a1a1a;">${payload.service} — ${heading}</div>
      </td>
    </tr>
    <tr>
      <td style="padding:20px 32px;border-bottom:1px solid #ebebeb;">
        <div style="font-size:13px;color:#888;margin-bottom:4px;">${longDate}</div>
        <div style="font-size:22px;font-weight:600;color:#1a1a1a;">${displayRange}</div>
        <div style="font-size:12px;color:#888;margin-top:4px;">${timezone}</div>
      </td>
    </tr>
    <tr>
      <td style="padding:20px 32px;border-bottom:1px solid #ebebeb;">
        <table width="100%" cellspacing="0" cellpadding="0" style="font-size:13px;color:#333;line-height:1.6;">
          <tr>
            <td style="width:120px;color:#888;padding:4px 0;vertical-align:top;">${this.translate.instant('APPOINTMENT_BOOKING.MAIL.CONFIRMATION_ID')}</td>
            <td style="padding:4px 0;font-weight:700;color:#1a1a1a;letter-spacing:0.04em;">${this.reservationId}</td>
          </tr>
          <tr>
            <td style="color:#888;padding:4px 0;vertical-align:top;">${this.translate.instant('APPOINTMENT_BOOKING.MAIL.NAME')}</td>
            <td style="padding:4px 0;">${payload.name || ''}</td>
          </tr>
          <tr>
            <td style="color:#888;padding:4px 0;vertical-align:top;">${this.translate.instant('APPOINTMENT_BOOKING.MAIL.SERVICE')}</td>
            <td style="padding:4px 0;">${payload.service || ''}</td>
          </tr>
          ${phoneRow}
          ${notesRow}
          ${meetingRow}
        </table>
      </td>
    </tr>
    <tr>
      <td style="padding:20px 32px;border-bottom:1px solid #ebebeb;text-align:center;">
        ${meetingUrl ? `<a href="${meetingUrl}" style="display:inline-block;padding:10px 22px;background:#1a3d8f;color:#fff;text-decoration:none;font-size:13px;font-weight:600;border-radius:4px;margin:0 6px 8px;">${this.translate.instant('APPOINTMENT_BOOKING.MAIL.JOIN')}</a>` : ''}
        <a href="${editLink}" style="display:inline-block;padding:10px 22px;background:#f0f0f0;color:#1a1a1a;text-decoration:none;font-size:13px;font-weight:600;border-radius:4px;margin:0 6px 8px;">${this.translate.instant('APPOINTMENT_BOOKING.MAIL.UPDATE')}</a>
        <a href="${cancelLink}" style="display:inline-block;padding:10px 22px;background:#f0f0f0;color:#c0392b;text-decoration:none;font-size:13px;font-weight:600;border-radius:4px;margin:0 6px 8px;">${this.translate.instant('APPOINTMENT_BOOKING.MAIL.CANCEL')}</a>
      </td>
    </tr>
    <tr>
      <td style="padding:16px 32px;text-align:right;font-size:11px;color:#aaa;">
        Powered by Sagel &nbsp;·&nbsp; ID: ${this.reservationId}
      </td>
    </tr>
  </table>
</div>`;

    this.http
      .post(`${environment.baseUrl}/email_notifications/info`, {
        header: subject,
        content: reservationTicketEmail(html,this.ticketLink,this.translate.instant('RESERVATION_TICKET.VIEW_MANAGE')),
        to_addr: payload.customer_email,
      })
      .subscribe({ next: () => {}, error: () => {} });
  }
}
